import React, { useState, useEffect, useMemo } from "react";
import TableSkeleton from "../../../../../component/TableSkeleton";
import { Avatar, Box, Stack, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { useParams } from "react-router";
import TopBar from "../../../../../component/TopBar";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import FormField from "../../../../../component/Formfield";
import EmptyState from "../../../../../component/EmptyState";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../component/listing";
import { getTimesheetById, reviewTimesheet } from "../../../../../services/timesheet";
import { fetchEmployeesByIdArray } from "../../../../../services/employee";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, radius } from "../../../../../theme";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import { canReviewTimesheet } from "./reviewGate";

const STATUS_TONE = { incomplete: "muted", pending: "warning", approved: "success", rejected: "danger" };
const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const Field = ({ label, children }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{children ?? "—"}</Box>
  </Box>
);

const TimesheetDetail = () => {
  const { id } = useParams();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [ts, setTs] = useState(null);
  const { data: self } = useSelfEmployee();
  const [employee, setEmployee] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getTimesheetById(id);
        if (res?.success === false) throw new Error(res?.message || "Failed to load timesheet");
        const data = res?.data || null;
        if (!active) return;
        setTs(data);
        if (data?.employee) {
          const empRes = await fetchEmployeesByIdArray([data.employee]);
          if (active) setEmployee(empRes?.data?.[0] || null);
        }
      } catch (e) {
        if (active) notify.error(e?.message || "Failed to load timesheet");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = employee ? `${employee.fname || ""} ${employee.lname || ""}`.trim() : "Unknown";
  const sortedActivities = useMemo(
    () => [...(ts?.activities || [])].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [ts]
  );

  const handleReview = async (status) => {
    setSubmitting(true);
    try {
      const res = await reviewTimesheet(id, { status, reviewNotes: reviewNotes.trim() });
      if (res?.success === false) throw new Error(res?.message || "Failed to review timesheet");
      notify.success(`Timesheet ${status}`);
      setRefresh((p) => !p);
    } catch (e) {
      notify.error(e?.message || "Failed to review timesheet");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    // The header and the column widths are known before the data is, so they
    // render immediately and nothing shifts when the rows land.
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading={"Timesheets"} />
        <Box sx={{ p: "1.25rem" }}>
          <Box
            sx={{
              background: tokens.card,
              border: `1px solid ${tokens.border}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table sx={{ width: "100%", tableLayout: "fixed" }} aria-busy="true">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Date</TableCell>
                  <TableCell sx={headCellSx}>Project</TableCell>
                  <TableCell sx={headCellSx}>Task</TableCell>
                  <TableCell sx={headCellSx}>Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody><TableSkeleton columns={4} /></TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
    );
  }
  if (!ts) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Timesheets"} /><EmptyState title="Timesheet not found" /></Box>;

  /*
    You cannot review your own week — not as an employee, and not as a manager
    looking at your own timesheet. The server enforces it; the screen was
    showing the buttons to everyone, so the submitter saw two controls that
    403 on click.
  */
  const canReview = canReviewTimesheet(ts, self);
  const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.25rem" };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Timesheets"} breadcrumbs={[{ label: "Timesheet", href: "/app/timesheet" }, { label: "Detail" }]} />

      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar src={employee?.profileImage || undefined} sx={{ width: "3rem", height: "3rem", bgcolor: tokens.accent, fontWeight: 600 }}>{initialsOf(name)}</Avatar>
            <Box>
              <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{fmt(ts.startDate)} – {fmt(ts.endDate)}</Box>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BtnSecondary to="/app/timesheet" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
            <StatusChip label={ts.status} tone={STATUS_TONE[ts.status]} />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={4} sx={{ mt: "1.25rem", pt: "1.25rem", borderTop: `1px solid ${tokens.border}`, flexWrap: "wrap" }}>
          <Field label="Total Hours">{ts.totalHours || 0} h</Field>
          <Field label="Regular">{ts.regularHours || 0} h</Field>
          <Field label="Overtime">{ts.overtimeHours || 0} h</Field>
          <Field label="Activities">{ts.activities?.length || 0}</Field>
        </Stack>
      </Box>

      <Box sx={cardSx}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "1rem" }}>Activities</Box>
        {sortedActivities.length === 0 ? (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No activities logged.</Box>
        ) : (
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Date</TableCell>
                <TableCell sx={headCellSx}>Project</TableCell>
                <TableCell sx={headCellSx}>Task</TableCell>
                <TableCell sx={headCellSx}>Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActivities.map((a, i) => (
                <TableRow key={i}>
                  <TableCell sx={bodyCellSx}>{fmt(a.date)}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{a.project || "—"}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{a.taskName || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{a.hours} h</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box sx={cardSx}>
        {canReview ? (
          <>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Review notes</Box>
            <FormField type="text" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} fullwidth row={2} />
            <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: "1.25rem" }}>
              <BtnSecondary onClick={() => handleReview("rejected")} disabled={submitting}
                sx={{ height: "3rem", px: "2rem", borderRadius: "10px", color: tokens.danger, borderColor: tokens.dangerBorder }}>Reject</BtnSecondary>
              <BtnPrimary onClick={() => handleReview("approved")} disabled={submitting} sx={{ borderRadius: "8px" }}>Approve</BtnPrimary>
            </Stack>
          </>
        ) : (
          <Field label="Review notes"><Box sx={{ fontWeight: 400 }}>{ts.reviewNotes || "—"}</Box></Field>
        )}
      </Box>
    </Box>
  );
};

export default TimesheetDetail;
